import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Maximize2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/shared/ui/Dialog";
import { InlineImageBlock, InlineVideoBlock } from "./ChatInlineMediaBlocks";
import { renderInline } from "./chatInlineFormatting";
import {
  findImageHeaderIndex,
  isImageUrl,
  isVideoUrl,
  type ParsedMarkdownTable,
} from "./chatMarkdownUtils";

export function ProductTableCard({ headers, rows }: ParsedMarkdownTable) {
  const navigate = useNavigate();
  const imageColIndex = findImageHeaderIndex(headers);
  const displayHeaders = imageColIndex >= 0 ? headers.filter((_, i) => i !== imageColIndex) : headers;

  return (
    <div className="my-2 rounded-xl border border-border/60 bg-muted/20 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Products • {rows.length} items
        </p>
        <button
          type="button"
          onClick={() => navigate("/products")}
          className="inline-flex items-center gap-1 rounded-md border border-border/70 bg-background/70 px-2.5 py-1 text-[11px] font-medium text-foreground transition-colors hover:bg-background"
        >
          View all
          <ExternalLink className="h-3 w-3" />
        </button>
      </div>
      <div className="mt-2 space-y-1.5">
        {rows.slice(0, 3).map((row, rowIndex) => {
          const imageUrl = imageColIndex >= 0 ? row[imageColIndex]?.trim() : undefined;
          const hasImage = imageUrl && isImageUrl(imageUrl);
          return (
            <button
              type="button"
              key={rowIndex}
              className="w-full rounded-lg border border-border/50 bg-background/60 p-2 text-left cursor-pointer transition-colors hover:bg-background/80 hover:border-primary/30"
              onClick={() => navigate("/products")}
            >
              <div className={hasImage ? "flex gap-3" : ""}>
                {hasImage && (
                  <div className="shrink-0 w-14 h-14 rounded-lg overflow-hidden bg-muted/40 border border-border/30">
                    <img
                      src={imageUrl}
                      alt=""
                      className="w-full h-full object-cover"
                      loading="lazy"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = "none";
                      }}
                    />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  {displayHeaders.map((header, colIndex) => {
                    const origIndex =
                      imageColIndex >= 0 && colIndex >= imageColIndex ? colIndex + 1 : colIndex;
                    const cellValue = row[origIndex] || "—";
                    if (isImageUrl(cellValue)) return null;
                    return (
                      <div
                        key={`${rowIndex}-${origIndex}`}
                        className="flex items-start justify-between gap-2 py-0.5 text-[11px]"
                      >
                        <span className="text-muted-foreground">{header}</span>
                        <span className="text-right font-medium text-foreground/90">
                          {renderInline(cellValue, `product-${rowIndex}-${origIndex}`)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="flex items-center justify-end mt-1">
                <span className="text-[10px] text-primary/60 font-medium">View product →</span>
              </div>
            </button>
          );
        })}
        {rows.length > 3 && (
          <p className="text-[11px] text-muted-foreground">
            +{rows.length - 3} more products
          </p>
        )}
      </div>
    </div>
  );
}

function renderTableCell(
  raw: string,
  rowIndex: number,
  colIndex: number,
  mediaAutoPreview: boolean,
  cellCls: string,
) {
  const value = (raw ?? "").trim();
  if (mediaAutoPreview && value) {
    if (isImageUrl(value)) {
      return (
        <td key={`c-${rowIndex}-${colIndex}`} className={cellCls}>
          <InlineImageBlock url={value} compact />
        </td>
      );
    }
    if (isVideoUrl(value)) {
      return (
        <td key={`c-${rowIndex}-${colIndex}`} className={cellCls}>
          <InlineVideoBlock url={value} compact />
        </td>
      );
    }
  }
  return (
    <td
      key={`c-${rowIndex}-${colIndex}`}
      className={`${cellCls} text-foreground/90 break-words [overflow-wrap:anywhere]`}
    >
      {renderInline(raw || "—", `td-${rowIndex}-${colIndex}`)}
    </td>
  );
}

function isKeyValueTable(headers: string[], rows: string[][]): boolean {
  if (headers.length !== 2 || rows.length === 0) return false;
  const [left, right] = headers.map((h) => h.trim().toLowerCase());
  const keyHeaders = new Set(["field", "property", "setting", "item", "name", "metric"]);
  const valueHeaders = new Set(["value", "details", "detail", "status", "result"]);
  return keyHeaders.has(left) && valueHeaders.has(right);
}

function CompactKeyValueTable({ headers, rows }: ParsedMarkdownTable) {
  return (
    <div className="my-2 max-w-full overflow-hidden rounded-xl border border-border/45 bg-background/45">
      {rows.map((row, rowIndex) => {
        const key = row[0]?.trim() || headers[0] || "Field";
        const value = row[1]?.trim() || "—";
        return (
          <div
            key={rowIndex}
            className="border-b border-border/25 px-3 py-2.5 last:border-b-0"
          >
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground/75 break-words [overflow-wrap:anywhere]">
              {renderInline(key, `kv-k-${rowIndex}`)}
            </div>
            <div className="mt-1 text-[12px] font-medium leading-5 text-foreground/90 break-words [overflow-wrap:anywhere]">
              {renderInline(value, `kv-v-${rowIndex}`)}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export function PlainMarkdownTable({
  headers,
  rows,
  mediaAutoPreview,
  compact,
}: ParsedMarkdownTable & { mediaAutoPreview?: boolean; compact?: boolean }) {
  const [expanded, setExpanded] = useState(false);
  const autoPreview = mediaAutoPreview ?? false;
  if (compact && isKeyValueTable(headers, rows)) {
    return <CompactKeyValueTable headers={headers} rows={rows} />;
  }

  const inlineCellCls = compact
    ? "px-2 py-1.5 max-w-[140px] align-top"
    : "px-2.5 py-1.5 max-w-[180px]";
  const expandedCellCls = "px-3 py-2 align-top";
  const tableCls = compact
    ? "w-full table-fixed border-collapse text-[10px] sm:text-[11px] md:text-[12px]"
    : "w-full border-collapse text-[11px] sm:text-[12px] md:text-[13px]";
  const headerCls = compact
    ? "border-b border-border/40 px-2 py-1.5 text-left font-semibold text-foreground/80 break-words [overflow-wrap:anywhere]"
    : "border-b border-border/40 px-2.5 py-1.5 text-left font-semibold text-foreground/80 whitespace-nowrap";

  return (
    <div className="my-2 group/table relative min-w-0 max-w-full overflow-hidden">
      <div className="max-w-full overflow-x-auto rounded-lg border border-border/50">
        <table className={tableCls}>
          <thead className="bg-muted/30">
            <tr>
              {headers.map((header, i) => (
                <th key={`h-${i}`} className={headerCls}>
                  {renderInline(header, `th-${i}`)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, rowIndex) => (
              <tr
                key={rowIndex}
                className="border-b border-border/30 last:border-b-0 align-top"
              >
                {headers.map((_, colIndex) =>
                  renderTableCell(row[colIndex] ?? "", rowIndex, colIndex, autoPreview, inlineCellCls),
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button
        type="button"
        onClick={() => setExpanded(true)}
        aria-label="Expand table"
        className="absolute top-1.5 right-1.5 inline-flex items-center gap-1 rounded-md border border-border/60 bg-background/90 px-1.5 py-0.5 text-[10px] font-medium text-foreground/70 shadow-sm opacity-60 group-hover/table:opacity-100 hover:!opacity-100 hover:bg-background hover:text-foreground transition-opacity"
      >
        <Maximize2 className="w-3 h-3" />
        Expand
      </button>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="!max-w-[min(96vw,1100px)] max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-3 border-b border-border/40">
            <DialogTitle className="text-base">Table view</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto px-6 py-4 flex-1">
            <table className="w-full border-collapse text-[13px]">
              <thead className="sticky top-0 bg-card/95 backdrop-blur z-10">
                <tr>
                  {headers.map((header, i) => (
                    <th
                      key={`xh-${i}`}
                      className="border-b border-border/60 px-3 py-2 text-left font-semibold text-foreground/90 whitespace-nowrap"
                    >
                      {renderInline(header, `xth-${i}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, rowIndex) => (
                  <tr
                    key={rowIndex}
                    className="border-b border-border/30 last:border-b-0 align-top hover:bg-muted/20"
                  >
                    {headers.map((_, colIndex) =>
                      renderTableCell(row[colIndex] ?? "", rowIndex, colIndex, autoPreview, expandedCellCls),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
